import { deferred } from '@thinkalpha/ui-core/deferred';
import { injectable, inject } from 'inversify';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class TradingSocketProvider {
    #worker: Worker | SharedWorker;
    #log: ReactBindings['Logger'];
    #workerReady = deferred<void>();

    constructor(@inject('Logger') logger: ReactBindings['Logger']) {
        this.#log = logger.getSubLogger({ name: 'TradingSocketProvider' }, { component: 'trading' });

        if (typeof SharedWorker !== 'undefined') {
            if (TRACE) {
                this.#log.trace('SharedWorker available, creating connection');
            }
            this.#worker = new SharedWorker(new URL('./worker.ts', import.meta.url), {
                type: 'module',
                name: 'trading-socket-worker',
            });
            this.#workerReady.resolve();
        } else {
            if (TRACE) {
                this.#log.trace('SharedWorker not available, using singleton Worker');
            }
            this.#worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

            // Wait for the worker to be ready
            const readyHandler = (_event: MessageEvent) => {
                this.#workerReady.resolve();
                (this.#worker as Worker).removeEventListener('message', readyHandler);
            };
            (this.#worker as Worker).addEventListener('message', readyHandler);
        }

        // A worker 'error' event is the only signal for a script that failed to load
        // (e.g. stale asset after a deploy) or crashed during evaluation. Without this
        // listener that failure is silent and every request times out.
        this.#worker.addEventListener('error', this.#handleWorkerError);
    }

    readonly #handleWorkerError = (event: Event): void => {
        const { message, filename, lineno, colno } = event as ErrorEvent;
        this.#log.error('Trading socket worker error; the worker may have failed to load or crashed', {
            error: new Error(message || 'Unknown trading socket worker error'),
            filename,
            lineno,
            colno,
        });
    };

    async getWorkerPort(): Promise<MessagePort> {
        await this.#workerReady.promise;

        if (this.#worker instanceof SharedWorker) {
            return this.#worker.port;
        } else {
            // Dedicated Worker
            const channel = new MessageChannel();
            (this.#worker as Worker).postMessage({ type: 'new-client', port: channel.port2 }, [channel.port2]);
            return channel.port1;
        }
    }
}
